import React, { useRef } from "react";
import { useRootDispatch, useRootSelector } from "~redux/hooks";
import { closeView, viewSelector } from "~redux/slices/edstSlice";
import { useDragging } from "hooks/useDragging";
import {
  baseBorderColor,
  colorNameMap,
  edstFontDim,
  edstFontName,
  useFocused,
  usePixiOnClickOutside,
} from "@poscon/shared-frontend";
import type { Container as PixiContainer } from "pixi.js";
import { useZIndex } from "hooks/useZIndex";
import { MenuTitleBar } from "components/utils/MenuTitleBar";
import { cyan } from "~/utils/constants";
import { EdstButton } from "components/utils/EdstButton";
import { InteractiveContainer } from "../utils/InteractiveContainer";

const { width: fontWidth, height: fontHeight } = edstFontDim;

export const sortMenuWidth = 22 * fontWidth;

type SortMenuProps = {
  menu: "ACL_SORT_MENU" | "DEP_SORT_MENU";
  onSubmit: () => void;
  children: React.ReactNode;
  height: number;
};

export const SortMenu = ({ menu, onSubmit, children, height }: SortMenuProps) => {
  const dispatch = useRootDispatch();
  const viewProps = useRootSelector((state) => viewSelector(state, menu));

  const ref = useRef<PixiContainer>(null);
  const zIndex = useZIndex(menu, ref);
  const focused = useFocused(ref);
  const { startDrag } = useDragging(ref, menu, "mouseup");

  usePixiOnClickOutside(ref, () => dispatch(closeView(menu)));

  return (
    <InteractiveContainer
      ref={ref}
      x={viewProps.position.x}
      y={viewProps.position.y}
      zIndex={zIndex}
      sortableChildren
    >
      <MenuTitleBar
        title="Sort Menu"
        width={sortMenuWidth}
        onTitleMouseDown={startDrag}
        view={menu}
        fontName={edstFontName}
        fill={focused ? cyan : colorNameMap.mediumGrey}
        tint={colorNameMap.black}
        withX={false}
      />
      <container y={fontHeight + 2}>
        <graphics
          draw={(graphics) => {
            graphics.clear();
            graphics
              .rect(0, 0, sortMenuWidth, height + edstFontDim.height + 12)
              .fill(0)
              .stroke({ width: 1, color: baseBorderColor });
          }}
        />
        <container y={4}>{children}</container>
        <container y={height + 4}>
          <EdstButton x={4} text="OK" onmousedown={onSubmit} />
          <EdstButton
            x={sortMenuWidth - edstFontDim.width * 4 - 15}
            text="Exit"
            onmousedown={() => dispatch(closeView(menu))}
          />
        </container>
      </container>
    </InteractiveContainer>
  );
};
